import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Loader2 } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'error';
  content: string;
}

const INITIAL_MESSAGE = {
  id: 'initial',
  role: 'assistant',
  content: "Hello! I'm here to help you with any questions about our glass railing solutions. What would you like to know?"
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([INITIAL_MESSAGE]);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    const newUserMessage = { 
      id: Date.now().toString(), 
      role: 'user' as const, 
      content: userMessage 
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ message: userMessage })
      });

      const text = await response.text();
      if (!text) {
        throw new Error('Empty response from server');
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to get response');
      }

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.message
      }]);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'error',
        content: "I apologize, but I'm having trouble connecting. Please call us at (301) 433-5323 or email info@primeviewglass.com for immediate assistance."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-5 right-5 bg-brand-600 text-white p-4 rounded-2xl shadow-glow-lg hover:bg-brand-700 transition-all duration-200 hover:scale-105 z-50"
        aria-label="Open chat"
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 w-[360px] h-[520px] bg-slate-950 rounded-2xl shadow-2xl flex flex-col border border-white/10 z-50 overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-brand-700 to-brand-600 text-white flex justify-between items-center">
        <div>
          <h3 className="font-display font-bold text-base">Glass Railing Expert</h3>
          <p className="text-brand-100 text-xs">Primeview Glass Assistant</p>
        </div>
        <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white transition-colors p-1">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
              message.role === 'user'
                ? 'bg-brand-600 text-white ml-8 rounded-br-sm'
                : message.role === 'error'
                ? 'bg-red-500/20 text-red-200 mr-8 rounded-bl-sm'
                : 'bg-white/10 text-white/90 mr-8 rounded-bl-sm'
            }`}
          >
            <p className="whitespace-pre-wrap">{message.content}</p>
          </div>
        ))}

        {isLoading && (
          <div className="bg-white/10 px-4 py-3 rounded-2xl rounded-bl-sm mr-8 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-300" />
            <p className="text-white/60 text-sm">Thinking...</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about glass railings..."
            className="flex-1 px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm transition-all"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="bg-brand-600 text-white p-2.5 rounded-xl hover:bg-brand-700 transition-colors disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default Chatbot;
